// src/components/Portfolio/MarketBacktesting.tsx
// Historical Backtesting with What-If Simulator
// Expanded event database + portfolio impact simulation

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Paper, Chip, CircularProgress,
  Alert, Tooltip, IconButton, Divider, List, ListItem, ListItemIcon,
  ListItemText, Select, MenuItem, FormControl, InputLabel, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Slider, Collapse, Switch,
  FormControlLabel, TextField, Tabs, Tab,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon,
  Warning as WarningIcon, Info as InfoIcon, Timeline as TimelineIcon,
  CalendarMonth as CalendarIcon, ShowChart as ChartIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Assessment as AssessmentIcon, History as HistoryIcon,
  Refresh as RefreshIcon, PlayArrow as PlayIcon, Science as ScienceIcon,
  AttachMoney as MoneyIcon, TrendingFlat as FlatIcon, Speed as SpeedIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer, ComposedChart, LineChart, Line, AreaChart, Area,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ReferenceLine, ReferenceArea, Scatter, ScatterChart, ZAxis,
  Cell, Brush, PieChart, Pie,
} from 'recharts';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface MarketImpact {
  indicator: string;
  name: string;
  priorValue: number;
  peakValue: number;
  changePercent: number;
  daysToRecover: number;
}

interface ClimateEvent {
  id: string;
  name: string;
  type: string;
  date: string;
  endDate?: string;
  location: string;
  states: string[];
  description: string;
  economicLoss: number;
  insuredLoss: number;
  fatalities: number;
  marketImpacts: MarketImpact[];
  affectedSectors: string[];
  affectedCommodities: string[];
  lessonLearned: string;
  portfolioImpactMultiplier: number;
}

interface PortfolioItem {
  postal_code?: string;
  state?: string;
  asset_value?: number | string;
  hurricane?: number;
  flood?: number;
  wildfire?: number;
  earthquake?: number;
  drought?: number;
  heat?: number;
  winter?: number;
  tornado?: number;
  [key: string]: any;
}

interface MarketBacktestingProps {
  portfolio: {
    items: PortfolioItem[];
    name?: string;
  };
}

interface SimulationResult {
  event: ClimateEvent;
  exposedValue: number;
  exposedLocations: number;
  estimatedLoss: number;
  lossPercent: number;
  recoveryDays: number;
  affectedStates: string[];
  riskScore: number;
}

// ============================================================================
// HISTORICAL EVENTS DATABASE - 30+ MAJOR EVENTS
// ============================================================================

const HISTORICAL_EVENTS: ClimateEvent[] = [
  // HURRICANES
  {
    id: 'katrina-2005',
    name: 'Hurricane Katrina',
    type: 'hurricane',
    date: '2005-08-29',
    location: 'Gulf Coast (LA, MS, AL)',
    states: ['LA', 'MS', 'AL', 'FL'],
    description: 'Category 5 hurricane causing catastrophic damage to New Orleans. Largest natural disaster in US history at the time.',
    economicLoss: 186.3,
    insuredLoss: 65.0,
    fatalities: 1836,
    marketImpacts: [
      { indicator: 'DCOILWTICO', name: 'Crude Oil (WTI)', priorValue: 64.5, peakValue: 70.85, changePercent: 9.8, daysToRecover: 45 },
      { indicator: 'MHHNGSP', name: 'Natural Gas', priorValue: 8.5, peakValue: 14.2, changePercent: 67.1, daysToRecover: 120 },
      { indicator: 'GASREGW', name: 'Gasoline', priorValue: 2.45, peakValue: 3.07, changePercent: 25.3, daysToRecover: 60 },
      { indicator: 'WPU081', name: 'Lumber', priorValue: 285, peakValue: 348, changePercent: 22.1, daysToRecover: 180 },
    ],
    affectedSectors: ['Energy', 'Insurance', 'Homebuilders', 'Utilities'],
    affectedCommodities: ['Natural Gas', 'Crude Oil', 'Gasoline', 'Lumber'],
    lessonLearned: 'Concentrated Gulf Coast energy infrastructure creates systemic risk. 25% of US oil production and 45% of refining capacity affected.',
    portfolioImpactMultiplier: 0.15
  },
  {
    id: 'sandy-2012',
    name: 'Hurricane Sandy',
    type: 'hurricane',
    date: '2012-10-29',
    location: 'Northeast (NJ, NY, CT)',
    states: ['NJ', 'NY', 'CT', 'PA', 'MA', 'RI'],
    description: 'Superstorm combining hurricane and nor\'easter, causing massive flooding in NYC metro area.',
    economicLoss: 81.9,
    insuredLoss: 36.0,
    fatalities: 285,
    marketImpacts: [
      { indicator: 'DCOILWTICO', name: 'Crude Oil (WTI)', priorValue: 85.5, peakValue: 88.1, changePercent: 3.0, daysToRecover: 14 },
      { indicator: 'GASREGW', name: 'Gasoline', priorValue: 3.45, peakValue: 3.68, changePercent: 6.7, daysToRecover: 21 },
      { indicator: 'VIXCLS', name: 'VIX', priorValue: 15.8, peakValue: 18.6, changePercent: 17.7, daysToRecover: 10 },
    ],
    affectedSectors: ['Insurance', 'Utilities', 'Transit', 'Retail', 'REITs'],
    affectedCommodities: ['Gasoline', 'Heating Oil', 'Electricity'],
    lessonLearned: 'Critical infrastructure vulnerability in major metro areas. NYSE closed for 2 days - first weather closure since 1888.',
    portfolioImpactMultiplier: 0.12
  },
  {
    id: 'harvey-2017',
    name: 'Hurricane Harvey',
    type: 'hurricane',
    date: '2017-08-25',
    location: 'Texas Gulf Coast (Houston)',
    states: ['TX', 'LA'],
    description: 'Category 4 hurricane with unprecedented rainfall (60+ inches). Flooded Houston metro area.',
    economicLoss: 148.8,
    insuredLoss: 40.0,
    fatalities: 107,
    marketImpacts: [
      { indicator: 'DCOILWTICO', name: 'Crude Oil (WTI)', priorValue: 47.2, peakValue: 49.5, changePercent: 4.9, daysToRecover: 30 },
      { indicator: 'GASREGW', name: 'Gasoline', priorValue: 2.35, peakValue: 2.67, changePercent: 13.6, daysToRecover: 45 },
      { indicator: 'MHHNGSP', name: 'Natural Gas', priorValue: 2.9, peakValue: 3.15, changePercent: 8.6, daysToRecover: 21 },
    ],
    affectedSectors: ['Energy', 'Insurance', 'Petrochemicals', 'Homebuilders'],
    affectedCommodities: ['Gasoline', 'Crude Oil', 'Natural Gas', 'Lumber'],
    lessonLearned: 'Houston\'s lack of zoning created $125B+ in damages. 30% of US refining capacity offline.',
    portfolioImpactMultiplier: 0.14
  },
  {
    id: 'irma-2017',
    name: 'Hurricane Irma',
    type: 'hurricane',
    date: '2017-09-10',
    location: 'Florida, Caribbean',
    states: ['FL', 'GA', 'SC'],
    description: 'Category 5 hurricane devastating Caribbean islands and causing widespread Florida damage.',
    economicLoss: 77.2,
    insuredLoss: 32.0,
    fatalities: 134,
    marketImpacts: [
      { indicator: 'DCOILWTICO', name: 'Crude Oil (WTI)', priorValue: 47.5, peakValue: 50.2, changePercent: 5.7, daysToRecover: 21 },
      { indicator: 'OJ', name: 'Orange Juice', priorValue: 135, peakValue: 158, changePercent: 17.0, daysToRecover: 180 },
    ],
    affectedSectors: ['Insurance', 'Agriculture', 'Tourism', 'Homebuilders'],
    affectedCommodities: ['Orange Juice', 'Lumber', 'Sugar'],
    lessonLearned: 'Back-to-back hurricanes strained insurance industry. Florida citrus lost 70% of crop.',
    portfolioImpactMultiplier: 0.11
  },
  {
    id: 'maria-2017',
    name: 'Hurricane Maria',
    type: 'hurricane',
    date: '2017-09-20',
    location: 'Puerto Rico, US Virgin Islands',
    states: ['PR', 'VI'],
    description: 'Category 5 hurricane causing complete grid failure in Puerto Rico. Deadliest US storm in 100 years.',
    economicLoss: 107.1,
    insuredLoss: 30.0,
    fatalities: 2975,
    marketImpacts: [
      { indicator: 'PHARMA', name: 'Pharma Supply', priorValue: 100, peakValue: 88, changePercent: -12.0, daysToRecover: 365 },
    ],
    affectedSectors: ['Pharmaceuticals', 'Medical Devices', 'Utilities', 'Municipal Bonds'],
    affectedCommodities: ['Pharmaceuticals', 'Medical Supplies'],
    lessonLearned: 'Puerto Rico produces 10% of US pharmaceuticals. Grid took 11 months to restore.',
    portfolioImpactMultiplier: 0.08
  },
  {
    id: 'michael-2018',
    name: 'Hurricane Michael',
    type: 'hurricane',
    date: '2018-10-10',
    location: 'Florida Panhandle',
    states: ['FL', 'GA', 'AL'],
    description: 'Category 5 hurricane - strongest to hit Florida Panhandle. Devastated Mexico Beach.',
    economicLoss: 31.3,
    insuredLoss: 15.5,
    fatalities: 74,
    marketImpacts: [
      { indicator: 'WPU081', name: 'Lumber', priorValue: 365, peakValue: 395, changePercent: 8.2, daysToRecover: 60 },
      { indicator: 'COTTON', name: 'Cotton', priorValue: 78, peakValue: 85, changePercent: 9.0, daysToRecover: 90 },
    ],
    affectedSectors: ['Agriculture', 'Military', 'Homebuilders', 'Insurance'],
    affectedCommodities: ['Cotton', 'Timber', 'Peanuts', 'Lumber'],
    lessonLearned: 'First Cat 5 to hit US since Andrew. $5B in timber losses.',
    portfolioImpactMultiplier: 0.09
  },
  {
    id: 'ida-2021',
    name: 'Hurricane Ida',
    type: 'hurricane',
    date: '2021-08-29',
    location: 'Louisiana, Northeast',
    states: ['LA', 'MS', 'NJ', 'NY', 'PA'],
    description: 'Category 4 hurricane hitting Louisiana, then causing catastrophic Northeast flooding.',
    economicLoss: 78.5,
    insuredLoss: 36.0,
    fatalities: 96,
    marketImpacts: [
      { indicator: 'DCOILWTICO', name: 'Crude Oil (WTI)', priorValue: 68.5, peakValue: 73.2, changePercent: 6.9, daysToRecover: 21 },
      { indicator: 'MHHNGSP', name: 'Natural Gas', priorValue: 4.2, peakValue: 4.85, changePercent: 15.5, daysToRecover: 35 },
    ],
    affectedSectors: ['Energy', 'Insurance', 'Utilities', 'Transit'],
    affectedCommodities: ['Crude Oil', 'Natural Gas', 'Gasoline'],
    lessonLearned: 'Dual-region impact created compounding effects. 95% of Gulf oil production offline.',
    portfolioImpactMultiplier: 0.10
  },
  {
    id: 'ian-2022',
    name: 'Hurricane Ian',
    type: 'hurricane',
    date: '2022-09-28',
    location: 'Florida (Fort Myers)',
    states: ['FL', 'SC', 'NC'],
    description: 'Category 4 hurricane causing catastrophic damage to Fort Myers and Southwest Florida.',
    economicLoss: 112.9,
    insuredLoss: 60.0,
    fatalities: 157,
    marketImpacts: [
      { indicator: 'WPU081', name: 'Lumber', priorValue: 485, peakValue: 545, changePercent: 12.4, daysToRecover: 90 },
      { indicator: 'OJ', name: 'Orange Juice', priorValue: 185, peakValue: 225, changePercent: 21.6, daysToRecover: 365 },
    ],
    affectedSectors: ['Insurance', 'Homebuilders', 'Agriculture', 'Tourism'],
    affectedCommodities: ['Orange Juice', 'Lumber', 'Citrus'],
    lessonLearned: 'Second-costliest hurricane ever. Multiple FL insurers went insolvent.',
    portfolioImpactMultiplier: 0.13
  },

  // WINTER STORMS
  {
    id: 'texas-freeze-2021',
    name: 'Texas Winter Storm Uri',
    type: 'winter',
    date: '2021-02-13',
    location: 'Texas (statewide)',
    states: ['TX', 'OK', 'LA', 'AR'],
    description: 'Polar vortex caused unprecedented cold. Grid failure left millions without power.',
    economicLoss: 130.0,
    insuredLoss: 23.0,
    fatalities: 246,
    marketImpacts: [
      { indicator: 'MHHNGSP', name: 'Natural Gas', priorValue: 2.85, peakValue: 23.86, changePercent: 737.2, daysToRecover: 14 },
      { indicator: 'DCOILWTICO', name: 'Crude Oil (WTI)', priorValue: 59.5, peakValue: 63.2, changePercent: 6.2, daysToRecover: 7 },
      { indicator: 'ELECTRICITY', name: 'ERCOT Power', priorValue: 50, peakValue: 9000, changePercent: 17900.0, daysToRecover: 5 },
    ],
    affectedSectors: ['Energy', 'Utilities', 'Insurance', 'Retail'],
    affectedCommodities: ['Natural Gas', 'Propane', 'Crude Oil', 'Electricity'],
    lessonLearned: 'ERCOT grid isolation created catastrophic failure. Spot power hit $9,000/MWh.',
    portfolioImpactMultiplier: 0.12
  },
  {
    id: 'polar-vortex-2014',
    name: 'Polar Vortex 2014',
    type: 'winter',
    date: '2014-01-06',
    location: 'Midwest and Northeast',
    states: ['MN', 'WI', 'IL', 'MI', 'OH', 'IN', 'PA', 'NY'],
    description: 'Record cold across Midwest and Northeast. Wind chills below -50°F.',
    economicLoss: 5.0,
    insuredLoss: 1.7,
    fatalities: 21,
    marketImpacts: [
      { indicator: 'MHHNGSP', name: 'Natural Gas', priorValue: 4.2, peakValue: 6.1, changePercent: 45.2, daysToRecover: 30 },
      { indicator: 'PROPANE', name: 'Propane', priorValue: 1.05, peakValue: 4.25, changePercent: 304.8, daysToRecover: 45 },
    ],
    affectedSectors: ['Energy', 'Utilities', 'Retail', 'Transportation'],
    affectedCommodities: ['Natural Gas', 'Propane', 'Heating Oil'],
    lessonLearned: 'Propane shortage exposed supply chain vulnerabilities. GDP impact ~0.3%.',
    portfolioImpactMultiplier: 0.06
  },
  {
    id: 'winter-storm-2022',
    name: 'Winter Storm Elliott',
    type: 'winter',
    date: '2022-12-22',
    location: 'Eastern US',
    states: ['NY', 'PA', 'OH', 'MI', 'WV', 'VA', 'NC', 'TN', 'KY'],
    description: 'Bomb cyclone bringing blizzard conditions during Christmas.',
    economicLoss: 6.5,
    insuredLoss: 2.5,
    fatalities: 87,
    marketImpacts: [
      { indicator: 'MHHNGSP', name: 'Natural Gas', priorValue: 5.8, peakValue: 7.2, changePercent: 24.1, daysToRecover: 14 },
    ],
    affectedSectors: ['Airlines', 'Retail', 'Utilities', 'Energy'],
    affectedCommodities: ['Natural Gas', 'Heating Oil', 'Jet Fuel'],
    lessonLearned: 'Over 15,000 flights cancelled. Buffalo received 50+ inches of snow.',
    portfolioImpactMultiplier: 0.05
  },

  // WILDFIRES
  {
    id: 'camp-fire-2018',
    name: 'Camp Fire (Paradise)',
    type: 'wildfire',
    date: '2018-11-08',
    location: 'California (Butte County)',
    states: ['CA'],
    description: 'Most destructive wildfire in California history. Town of Paradise destroyed.',
    economicLoss: 16.5,
    insuredLoss: 12.5,
    fatalities: 85,
    marketImpacts: [
      { indicator: 'PCU_UTIL', name: 'PG&E Stock', priorValue: 47.5, peakValue: 6.2, changePercent: -86.9, daysToRecover: 1825 },
      { indicator: 'CREDIT', name: 'PG&E Credit Spreads', priorValue: 150, peakValue: 2500, changePercent: 1566.7, daysToRecover: 730 },
    ],
    affectedSectors: ['Utilities', 'Insurance', 'Homebuilders'],
    affectedCommodities: ['Lumber', 'Carbon Credits'],
    lessonLearned: 'PG&E filed bankruptcy ($30B+ liabilities). Inverse condemnation creates unlimited utility liability.',
    portfolioImpactMultiplier: 0.08
  },
  {
    id: 'tubbs-fire-2017',
    name: 'Tubbs Fire (Wine Country)',
    type: 'wildfire',
    date: '2017-10-08',
    location: 'California (Sonoma, Napa)',
    states: ['CA'],
    description: 'Devastating fire in Wine Country destroying 5,600+ structures.',
    economicLoss: 14.5,
    insuredLoss: 10.0,
    fatalities: 22,
    marketImpacts: [
      { indicator: 'WINE', name: 'Wine Industry', priorValue: 100, peakValue: 92, changePercent: -8.0, daysToRecover: 365 },
    ],
    affectedSectors: ['Wine/Agriculture', 'Tourism', 'Utilities', 'Insurance'],
    affectedCommodities: ['Wine', 'Grapes'],
    lessonLearned: 'Wine Country tourism dropped 40%. Fire started by electrical equipment.',
    portfolioImpactMultiplier: 0.07
  },
  {
    id: 'dixie-fire-2021',
    name: 'Dixie Fire',
    type: 'wildfire',
    date: '2021-07-13',
    location: 'California (Butte, Plumas)',
    states: ['CA'],
    description: 'Largest single fire in California history. Destroyed town of Greenville.',
    economicLoss: 1.5,
    insuredLoss: 0.7,
    fatalities: 1,
    marketImpacts: [
      { indicator: 'CARBON', name: 'CA Carbon Credits', priorValue: 18, peakValue: 28, changePercent: 55.6, daysToRecover: 180 },
    ],
    affectedSectors: ['Forestry', 'Utilities', 'Agriculture'],
    affectedCommodities: ['Timber', 'Carbon Credits'],
    lessonLearned: 'Burned 963,000 acres - larger than Rhode Island.',
    portfolioImpactMultiplier: 0.04
  },
  {
    id: 'maui-fire-2023',
    name: 'Maui Wildfires (Lahaina)',
    type: 'wildfire',
    date: '2023-08-08',
    location: 'Hawaii (Maui)',
    states: ['HI'],
    description: 'Deadliest US wildfire in over 100 years. Historic Lahaina destroyed.',
    economicLoss: 6.0,
    insuredLoss: 3.5,
    fatalities: 100,
    marketImpacts: [
      { indicator: 'TOURISM', name: 'Hawaii Tourism', priorValue: 100, peakValue: 75, changePercent: -25.0, daysToRecover: 365 },
      { indicator: 'HAWAIIAN', name: 'Hawaiian Electric', priorValue: 40, peakValue: 12, changePercent: -70.0, daysToRecover: 730 },
    ],
    affectedSectors: ['Tourism', 'Utilities', 'Insurance', 'Hospitality'],
    affectedCommodities: [],
    lessonLearned: 'Drought and hurricane winds created fire conditions. Hawaiian Electric faced bankruptcy.',
    portfolioImpactMultiplier: 0.06
  },

  // FLOODS
  {
    id: 'midwest-floods-2019',
    name: 'Midwest Floods',
    type: 'flood',
    date: '2019-03-13',
    location: 'Midwest (NE, IA, MO, KS)',
    states: ['NE', 'IA', 'MO', 'KS', 'SD', 'MN'],
    description: 'Historic flooding along Missouri and Mississippi rivers.',
    economicLoss: 20.0,
    insuredLoss: 3.0,
    fatalities: 12,
    marketImpacts: [
      { indicator: 'CORN', name: 'Corn Futures', priorValue: 3.65, peakValue: 4.45, changePercent: 21.9, daysToRecover: 120 },
      { indicator: 'SOYBEAN', name: 'Soybean Futures', priorValue: 8.85, peakValue: 9.45, changePercent: 6.8, daysToRecover: 90 },
    ],
    affectedSectors: ['Agriculture', 'Food & Beverage', 'Transportation'],
    affectedCommodities: ['Corn', 'Soybeans', 'Wheat', 'Cattle'],
    lessonLearned: 'Prevented planting of 19.4M acres - largest in history.',
    portfolioImpactMultiplier: 0.07
  },
  {
    id: 'louisiana-floods-2016',
    name: 'Louisiana Floods',
    type: 'flood',
    date: '2016-08-11',
    location: 'Louisiana (Baton Rouge)',
    states: ['LA'],
    description: 'Historic flooding from slow-moving storm. "Worst US disaster since Sandy."',
    economicLoss: 10.0,
    insuredLoss: 3.1,
    fatalities: 13,
    marketImpacts: [
      { indicator: 'CORN', name: 'Corn Futures', priorValue: 3.35, peakValue: 3.52, changePercent: 5.1, daysToRecover: 45 },
    ],
    affectedSectors: ['Insurance', 'Agriculture', 'Homebuilders'],
    affectedCommodities: ['Cotton', 'Corn', 'Sugar'],
    lessonLearned: '90% of damaged homes had no flood insurance.',
    portfolioImpactMultiplier: 0.05
  },
  {
    id: 'kentucky-floods-2022',
    name: 'Kentucky Floods',
    type: 'flood',
    date: '2022-07-26',
    location: 'Eastern Kentucky',
    states: ['KY'],
    description: 'Flash flooding destroyed communities in Appalachian region.',
    economicLoss: 1.5,
    insuredLoss: 0.3,
    fatalities: 44,
    marketImpacts: [
      { indicator: 'COAL', name: 'Coal', priorValue: 145, peakValue: 155, changePercent: 6.9, daysToRecover: 30 },
    ],
    affectedSectors: ['Coal Mining', 'Insurance'],
    affectedCommodities: ['Coal'],
    lessonLearned: 'Low insurance penetration in rural areas. Climate increasing flash flood intensity.',
    portfolioImpactMultiplier: 0.03
  },

  // DROUGHTS
  {
    id: 'drought-2012',
    name: 'US Drought of 2012',
    type: 'drought',
    date: '2012-05-01',
    location: 'Central US (Corn Belt)',
    states: ['NE', 'KS', 'OK', 'TX', 'IA', 'MO', 'IL', 'IN', 'AR'],
    description: 'Worst drought since 1956. 80% of agricultural land affected.',
    economicLoss: 33.9,
    insuredLoss: 17.3,
    fatalities: 123,
    marketImpacts: [
      { indicator: 'CORN', name: 'Corn Futures', priorValue: 5.35, peakValue: 8.31, changePercent: 55.3, daysToRecover: 365 },
      { indicator: 'SOYBEAN', name: 'Soybean Futures', priorValue: 12.55, peakValue: 17.68, changePercent: 40.9, daysToRecover: 270 },
      { indicator: 'WHEAT', name: 'Wheat Futures', priorValue: 6.25, peakValue: 9.38, changePercent: 50.1, daysToRecover: 180 },
    ],
    affectedSectors: ['Agriculture', 'Food & Beverage', 'Livestock', 'Ethanol'],
    affectedCommodities: ['Corn', 'Soybeans', 'Wheat', 'Cattle', 'Ethanol'],
    lessonLearned: 'Food inflation hit 3.2%. US corn exports dropped 50%.',
    portfolioImpactMultiplier: 0.08
  },
  {
    id: 'california-drought-2021',
    name: 'Western Megadrought',
    type: 'drought',
    date: '2021-01-01',
    location: 'Western US',
    states: ['CA', 'NV', 'AZ', 'UT', 'CO', 'NM'],
    description: 'Worst drought in 1,200 years. Lake Mead at record lows.',
    economicLoss: 9.0,
    insuredLoss: 1.5,
    fatalities: 0,
    marketImpacts: [
      { indicator: 'ALMONDS', name: 'Almond Prices', priorValue: 2.15, peakValue: 2.85, changePercent: 32.6, daysToRecover: 365 },
      { indicator: 'CATTLE', name: 'Cattle Futures', priorValue: 122, peakValue: 138, changePercent: 13.1, daysToRecover: 180 },
    ],
    affectedSectors: ['Agriculture', 'Utilities', 'Municipal Water'],
    affectedCommodities: ['Almonds', 'Cattle', 'Lettuce', 'Water Rights'],
    lessonLearned: 'California produces 80% of US almonds. Colorado River compact renegotiation required.',
    portfolioImpactMultiplier: 0.06
  },
  {
    id: 'texas-drought-2011',
    name: 'Texas Drought & Wildfires',
    type: 'drought',
    date: '2011-01-01',
    location: 'Texas (statewide)',
    states: ['TX', 'OK', 'NM'],
    description: 'Record-breaking drought. Driest year in Texas history.',
    economicLoss: 14.0,
    insuredLoss: 5.2,
    fatalities: 95,
    marketImpacts: [
      { indicator: 'CATTLE', name: 'Cattle Futures', priorValue: 95, peakValue: 125, changePercent: 31.6, daysToRecover: 365 },
      { indicator: 'COTTON', name: 'Cotton Futures', priorValue: 150, peakValue: 215, changePercent: 43.3, daysToRecover: 180 },
    ],
    affectedSectors: ['Agriculture', 'Livestock', 'Insurance', 'Utilities'],
    affectedCommodities: ['Cattle', 'Cotton', 'Hay', 'Corn'],
    lessonLearned: 'Texas cattle herd reduced by 600,000 head. 4 million acres burned.',
    portfolioImpactMultiplier: 0.07
  },

  // HEAT WAVES
  {
    id: 'pacific-heat-dome-2021',
    name: 'Pacific Northwest Heat Dome',
    type: 'heat',
    date: '2021-06-25',
    location: 'Pacific Northwest',
    states: ['WA', 'OR', 'ID'],
    description: 'Record-shattering heat. Portland hit 116°F, Seattle 108°F.',
    economicLoss: 1.5,
    insuredLoss: 0.5,
    fatalities: 229,
    marketImpacts: [
      { indicator: 'WHEAT', name: 'Wheat Futures', priorValue: 6.85, peakValue: 7.45, changePercent: 8.8, daysToRecover: 45 },
      { indicator: 'ELECTRICITY', name: 'NW Power Prices', priorValue: 45, peakValue: 125, changePercent: 177.8, daysToRecover: 7 },
    ],
    affectedSectors: ['Utilities', 'Agriculture', 'Healthcare'],
    affectedCommodities: ['Wheat', 'Cherries', 'Electricity'],
    lessonLearned: 'Temperatures 30°F above normal. Cherry crop reduced 90%.',
    portfolioImpactMultiplier: 0.04
  },
  {
    id: 'texas-heat-2023',
    name: 'Texas Heat Wave 2023',
    type: 'heat',
    date: '2023-06-15',
    location: 'Texas (statewide)',
    states: ['TX', 'LA', 'OK', 'AR'],
    description: '40+ consecutive days above 100°F in many areas.',
    economicLoss: 5.0,
    insuredLoss: 1.0,
    fatalities: 300,
    marketImpacts: [
      { indicator: 'MHHNGSP', name: 'Natural Gas', priorValue: 2.45, peakValue: 2.95, changePercent: 20.4, daysToRecover: 30 },
      { indicator: 'ELECTRICITY', name: 'ERCOT Power', priorValue: 45, peakValue: 350, changePercent: 677.8, daysToRecover: 14 },
    ],
    affectedSectors: ['Utilities', 'Agriculture', 'Healthcare', 'Livestock'],
    affectedCommodities: ['Electricity', 'Natural Gas', 'Cattle'],
    lessonLearned: 'ERCOT near grid failure multiple times.',
    portfolioImpactMultiplier: 0.05
  },

  // TORNADOES
  {
    id: 'joplin-tornado-2011',
    name: 'Joplin Tornado',
    type: 'tornado',
    date: '2011-05-22',
    location: 'Missouri (Joplin)',
    states: ['MO'],
    description: 'EF5 tornado - deadliest since 1947. Destroyed 25% of Joplin.',
    economicLoss: 3.2,
    insuredLoss: 2.8,
    fatalities: 158,
    marketImpacts: [
      { indicator: 'WPU081', name: 'Lumber', priorValue: 285, peakValue: 315, changePercent: 10.5, daysToRecover: 90 },
    ],
    affectedSectors: ['Insurance', 'Healthcare', 'Homebuilders'],
    affectedCommodities: ['Lumber', 'Steel'],
    lessonLearned: 'Hospital destroyed with patients inside. Led to enhanced tornado warnings.',
    portfolioImpactMultiplier: 0.04
  },
  {
    id: 'super-outbreak-2011',
    name: '2011 Super Outbreak',
    type: 'tornado',
    date: '2011-04-25',
    location: 'Southeast (AL, MS, TN, GA)',
    states: ['AL', 'MS', 'TN', 'GA', 'VA', 'AR', 'KY'],
    description: 'Largest tornado outbreak in history - 360 tornadoes in 3 days.',
    economicLoss: 12.0,
    insuredLoss: 8.5,
    fatalities: 324,
    marketImpacts: [
      { indicator: 'WPU081', name: 'Lumber', priorValue: 295, peakValue: 335, changePercent: 13.6, daysToRecover: 120 },
      { indicator: 'COTTON', name: 'Cotton Futures', priorValue: 185, peakValue: 198, changePercent: 7.0, daysToRecover: 60 },
    ],
    affectedSectors: ['Insurance', 'Homebuilders', 'Agriculture', 'Utilities'],
    affectedCommodities: ['Lumber', 'Cotton', 'Steel'],
    lessonLearned: 'Tuscaloosa and Birmingham hit by same storm. Nuclear plant damaged.',
    portfolioImpactMultiplier: 0.06
  },
  {
    id: 'tornado-outbreak-2021',
    name: 'December Tornado Outbreak 2021',
    type: 'tornado',
    date: '2021-12-10',
    location: 'Central US',
    states: ['KY', 'AR', 'TN', 'IL', 'MO', 'MS'],
    description: 'Record-breaking December outbreak. Quad-State tornado traveled 250+ miles.',
    economicLoss: 5.0,
    insuredLoss: 3.8,
    fatalities: 90,
    marketImpacts: [
      { indicator: 'WPU081', name: 'Lumber', priorValue: 1050, peakValue: 1125, changePercent: 7.1, daysToRecover: 45 },
    ],
    affectedSectors: ['Insurance', 'Logistics', 'Manufacturing'],
    affectedCommodities: ['Lumber', 'Steel'],
    lessonLearned: 'Amazon warehouse collapsed. December tornadoes extremely rare.',
    portfolioImpactMultiplier: 0.04
  },

  // EARTHQUAKES
  {
    id: 'northridge-1994',
    name: 'Northridge Earthquake',
    type: 'earthquake',
    date: '1994-01-17',
    location: 'California (Los Angeles)',
    states: ['CA'],
    description: 'M6.7 earthquake. Costliest earthquake in US history.',
    economicLoss: 44.0,
    insuredLoss: 15.3,
    fatalities: 57,
    marketImpacts: [
      { indicator: 'VIXCLS', name: 'VIX', priorValue: 11.5, peakValue: 17.8, changePercent: 54.8, daysToRecover: 45 },
      { indicator: 'STEEL', name: 'Steel', priorValue: 280, peakValue: 315, changePercent: 12.5, daysToRecover: 180 },
    ],
    affectedSectors: ['Insurance', 'Construction', 'REITs', 'Healthcare'],
    affectedCommodities: ['Steel', 'Cement', 'Lumber', 'Glass'],
    lessonLearned: 'Led to California Earthquake Authority. Only 30% had earthquake insurance.',
    portfolioImpactMultiplier: 0.10
  },
  {
    id: 'napa-earthquake-2014',
    name: 'South Napa Earthquake',
    type: 'earthquake',
    date: '2014-08-24',
    location: 'California (Napa Valley)',
    states: ['CA'],
    description: 'M6.0 earthquake - largest Bay Area quake since 1989.',
    economicLoss: 1.0,
    insuredLoss: 0.4,
    fatalities: 1,
    marketImpacts: [
      { indicator: 'WINE', name: 'Wine Industry', priorValue: 100, peakValue: 95, changePercent: -5.0, daysToRecover: 180 },
    ],
    affectedSectors: ['Wine/Agriculture', 'Tourism', 'Insurance'],
    affectedCommodities: ['Wine'],
    lessonLearned: 'Significant wine barrel losses. Highlighted seismic retrofit needs.',
    portfolioImpactMultiplier: 0.03
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatCurrency = (value: number): string => {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

const formatPercent = (value: number): string => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

const getEventTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    hurricane: '#3B82F6',
    flood: '#06B6D4',
    wildfire: '#F97316',
    drought: '#EAB308',
    winter: '#8B5CF6',
    earthquake: '#6B7280',
    heat: '#EF4444',
    tornado: '#10B981',
  };
  return colors[type] || '#6B7280';
};

const getEventTypeIcon = (type: string): string => {
  const icons: Record<string, string> = {
    hurricane: '🌀',
    flood: '🌊',
    wildfire: '🔥',
    drought: '☀️',
    winter: '❄️',
    earthquake: '🌍',
    heat: '🌡️',
    tornado: '🌪️',
  };
  return icons[type] || '⚠️';
};

const generateEventPriceData = (event: ClimateEvent, indicator: string): any[] => {
  const impact = event.marketImpacts.find(m => m.indicator === indicator);
  if (!impact) return [];

  const data: any[] = [];
  const eventDate = new Date(event.date);
  const daysBeforeEvent = 30;
  const daysAfterEvent = Math.max(60, impact.daysToRecover + 30);

  for (let i = -daysBeforeEvent; i <= daysAfterEvent; i++) {
    const date = new Date(eventDate);
    date.setDate(date.getDate() + i);

    let value: number;
    if (i < 0) {
      const noise = (Math.random() - 0.5) * 0.02 * impact.priorValue;
      value = impact.priorValue + noise;
    } else if (i <= 14) {
      const progress = i / 14;
      const easeOut = 1 - Math.pow(1 - progress, 3);
      value = impact.priorValue + (impact.peakValue - impact.priorValue) * easeOut;
      value += (Math.random() - 0.5) * 0.03 * value;
    } else if (i <= impact.daysToRecover) {
      const recoveryProgress = (i - 14) / (impact.daysToRecover - 14);
      const easeIn = Math.pow(recoveryProgress, 2);
      value = impact.peakValue - (impact.peakValue - impact.priorValue) * easeIn;
      value += (Math.random() - 0.5) * 0.02 * value;
    } else {
      const noise = (Math.random() - 0.5) * 0.02 * impact.priorValue;
      value = impact.priorValue + noise;
    }

    data.push({
      date: date.toISOString().split('T')[0],
      dateLabel: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      value: Math.max(0, value),
      isEventPeriod: i >= 0 && i <= 14,
      isRecovery: i > 14 && i <= impact.daysToRecover,
    });
  }

  return data;
};
// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MarketBacktesting: React.FC<MarketBacktestingProps> = ({ portfolio }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedEventType, setSelectedEventType] = useState<string>('all');
  const [selectedEvent, setSelectedEvent] = useState<ClimateEvent | null>(null);
  const [selectedIndicator, setSelectedIndicator] = useState<string>('');
  const [timeRange, setTimeRange] = useState<[number, number]>([2010, 2024]);
  const [simulationResults, setSimulationResults] = useState<SimulationResult[]>([]);
  const [customSeverity, setCustomSeverity] = useState<number>(100);
  const [showAllEvents, setShowAllEvents] = useState(false);

  // Calculate portfolio metrics
  const portfolioMetrics = useMemo(() => {
    if (!portfolio?.items?.length) return { totalValue: 0, items: [], states: [], hazards: {} };

    let totalValue = 0;
    const states = new Set<string>();
    const hazards: Record<string, { avgScore: number; count: number; totalValue: number }> = {};
    const hazardTypes = ['hurricane', 'flood', 'wildfire', 'earthquake', 'drought', 'heat', 'winter', 'tornado'];

    portfolio.items.forEach(item => {
      const value = typeof item.asset_value === 'string'
        ? parseFloat(item.asset_value.replace(/[$,]/g, ''))
        : (item.asset_value || 0);
      totalValue += value;
      if (item.state) states.add(item.state);

      hazardTypes.forEach(hazard => {
        const score = parseFloat(String(item[hazard])) || 0;
        if (score > 0) {
          if (!hazards[hazard]) hazards[hazard] = { avgScore: 0, count: 0, totalValue: 0 };
          hazards[hazard].count++;
          hazards[hazard].totalValue += value;
          hazards[hazard].avgScore = (hazards[hazard].avgScore * (hazards[hazard].count - 1) + score) / hazards[hazard].count;
        }
      });
    });

    return { totalValue, items: portfolio.items, states: Array.from(states), hazards };
  }, [portfolio]);

  const filteredEvents = useMemo(() => {
    return HISTORICAL_EVENTS.filter(event => {
      const eventYear = new Date(event.date).getFullYear();
      const matchesType = selectedEventType === 'all' || event.type === selectedEventType;
      const matchesTime = eventYear >= timeRange[0] && eventYear <= timeRange[1];
      return matchesType && matchesTime;
    });
  }, [selectedEventType, timeRange]);

  const relevantEvents = useMemo(() => {
    return filteredEvents.filter(event => {
      const hasStateOverlap = event.states.some(s => portfolioMetrics.states.includes(s));
      const hasHazardExposure = portfolioMetrics.hazards[event.type]?.avgScore >= 3;
      return hasStateOverlap || hasHazardExposure;
    }).sort((a, b) => b.economicLoss - a.economicLoss);
  }, [filteredEvents, portfolioMetrics]);

  const chartData = useMemo(() => {
    if (!selectedEvent || !selectedIndicator) return [];
    return generateEventPriceData(selectedEvent, selectedIndicator);
  }, [selectedEvent, selectedIndicator]);

  useEffect(() => {
    if (selectedEvent && selectedEvent.marketImpacts.length > 0) {
      setSelectedIndicator(selectedEvent.marketImpacts[0].indicator);
    }
  }, [selectedEvent]);

  // What-If Simulation
  const runSimulation = () => {
    const results: SimulationResult[] = HISTORICAL_EVENTS.map(event => {
      const exposedItems = portfolioMetrics.items.filter(item => {
        const stateMatch = item.state && event.states.includes(item.state);
        const hazardScore = parseFloat(String(item[event.type])) || 0;
        return stateMatch || hazardScore >= 5;
      });

      const exposedValue = exposedItems.reduce((sum, item) => {
        const value = typeof item.asset_value === 'string'
          ? parseFloat(item.asset_value.replace(/[$,]/g, ''))
          : (item.asset_value || 0);
        return sum + value;
      }, 0);

      const severityMultiplier = customSeverity / 100;
      const hazardData = portfolioMetrics.hazards[event.type];
      const riskMultiplier = hazardData ? (hazardData.avgScore / 10) : 0.5;
      
      const estimatedLoss = exposedValue * event.portfolioImpactMultiplier * severityMultiplier * riskMultiplier;
      const lossPercent = portfolioMetrics.totalValue > 0 ? (estimatedLoss / portfolioMetrics.totalValue) * 100 : 0;
      const avgRecovery = event.marketImpacts.length > 0
        ? event.marketImpacts.reduce((sum, m) => sum + m.daysToRecover, 0) / event.marketImpacts.length : 90;

      return {
        event,
        exposedValue,
        exposedLocations: exposedItems.length,
        estimatedLoss,
        lossPercent,
        recoveryDays: Math.round(avgRecovery),
        affectedStates: event.states.filter(s => portfolioMetrics.states.includes(s)),
        riskScore: hazardData?.avgScore || 0,
      };
    }).filter(r => r.exposedLocations > 0 || showAllEvents)
      .sort((a, b) => b.estimatedLoss - a.estimatedLoss);

    setSimulationResults(results);
  };

  useEffect(() => {
    if (activeTab === 1 && portfolioMetrics.totalValue > 0) runSimulation();
  }, [activeTab, portfolioMetrics, customSeverity, showAllEvents]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1F2937', mb: 1 }}>
          <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Historical Backtesting & What-If Simulator
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B7280' }}>
          Analyze {HISTORICAL_EVENTS.length} historical climate events and simulate their impact on your portfolio.
        </Typography>
      </Box>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<HistoryIcon />} iconPosition="start" label={`Historical Events (${HISTORICAL_EVENTS.length})`} />
        <Tab icon={<ScienceIcon />} iconPosition="start" label="What-If Simulator" />
      </Tabs>

      {/* TAB 0: HISTORICAL EVENTS */}
      {activeTab === 0 && (
        <>
          {/* Portfolio Relevance Card */}
          {Object.keys(portfolioMetrics.hazards).length > 0 && (
            <Card sx={{ mb: 3, border: '2px solid #3B82F6' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#3B82F6' }} />
                  Your Portfolio's Historical Exposure
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(portfolioMetrics.hazards)
                    .sort((a, b) => b[1].avgScore - a[1].avgScore)
                    .slice(0, 4)
                    .map(([hazard, data]) => {
                      const matchingEvents = HISTORICAL_EVENTS.filter(e => e.type === hazard);
                      const totalLoss = matchingEvents.reduce((sum, e) => sum + e.economicLoss, 0);
                      return (
                        <Grid item xs={6} md={3} key={hazard}>
                          <Paper sx={{ p: 2, bgcolor: '#F9FAFB', textAlign: 'center' }}>
                            <Typography variant="h4">{getEventTypeIcon(hazard)}</Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{hazard}</Typography>
                            <Typography variant="caption" sx={{ color: '#6B7280', display: 'block' }}>
                              Score: {data.avgScore.toFixed(1)} | {matchingEvents.length} events
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#EF4444', fontWeight: 600 }}>
                              ${totalLoss.toFixed(0)}B historical losses
                            </Typography>
                          </Paper>
                        </Grid>
                      );
                    })}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Aggregate Stats */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Aggregate Impact ({timeRange[0]}-{timeRange[1]})
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: '#FEE2E2', textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#991B1B' }}>
                      ${filteredEvents.reduce((sum, e) => sum + e.economicLoss, 0).toFixed(0)}B
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#991B1B' }}>Total Economic Loss</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: '#FEF3C7', textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#92400E' }}>{filteredEvents.length}</Typography>
                    <Typography variant="caption" sx={{ color: '#92400E' }}>Major Events</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: '#D1FAE5', textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#065F46' }}>{relevantEvents.length}</Typography>
                    <Typography variant="caption" sx={{ color: '#065F46' }}>Relevant to Portfolio</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: '#E0E7FF', textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#3730A3' }}>
                      {filteredEvents.reduce((sum, e) => sum + e.fatalities, 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#3730A3' }}>Total Fatalities</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Loss by Type Chart */}
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={Object.entries(
                  filteredEvents.reduce((acc, e) => { acc[e.type] = (acc[e.type] || 0) + e.economicLoss; return acc; }, {} as Record<string, number>)
                ).map(([type, loss]) => ({ type: type.charAt(0).toUpperCase() + type.slice(1), loss })).sort((a, b) => b.loss - a.loss)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tickFormatter={(v) => `$${v}B`} />
                  <YAxis type="category" dataKey="type" width={80} tick={{ fontSize: 11 }} />
                  <RechartsTooltip formatter={(value: number) => [`$${value.toFixed(1)}B`, 'Loss']} />
                  <Bar dataKey="loss" radius={[0, 4, 4, 0]}>
                    {Object.keys(filteredEvents.reduce((acc, e) => { acc[e.type] = 1; return acc; }, {} as Record<string, number>)).map((type, i) => (
                      <Cell key={i} fill={getEventTypeColor(type)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Event List and Details */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Events</Typography>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select value={selectedEventType} onChange={(e) => setSelectedEventType(e.target.value)} displayEmpty>
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="hurricane">🌀 Hurricane</MenuItem>
                        <MenuItem value="flood">🌊 Flood</MenuItem>
                        <MenuItem value="wildfire">🔥 Wildfire</MenuItem>
                        <MenuItem value="drought">☀️ Drought</MenuItem>
                        <MenuItem value="winter">❄️ Winter</MenuItem>
                        <MenuItem value="heat">🌡️ Heat</MenuItem>
                        <MenuItem value="tornado">🌪️ Tornado</MenuItem>
                        <MenuItem value="earthquake">🌍 Earthquake</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ px: 2, mb: 2 }}>
                    <Slider value={timeRange} onChange={(e, v) => setTimeRange(v as [number, number])} valueLabelDisplay="auto" min={1994} max={2024}
                      marks={[{ value: 2000, label: '2000' }, { value: 2012, label: '2012' }, { value: 2024, label: '2024' }]} />
                  </Box>
                  <Box sx={{ maxHeight: 450, overflow: 'auto' }}>
                    {filteredEvents.map((event) => (
                      <Paper key={event.id} sx={{ p: 1.5, mb: 1, cursor: 'pointer',
                        border: selectedEvent?.id === event.id ? `2px solid ${getEventTypeColor(event.type)}` : '1px solid #E5E7EB',
                        bgcolor: relevantEvents.includes(event) ? '#F0FDF4' : 'white', '&:hover': { bgcolor: '#F9FAFB' } }}
                        onClick={() => setSelectedEvent(event)}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="h6">{getEventTypeIcon(event.type)}</Typography>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{event.name}</Typography>
                              <Typography variant="caption" sx={{ color: '#6B7280' }}>{new Date(event.date).getFullYear()} • {event.location}</Typography>
                            </Box>
                          </Box>
                          <Chip label={`$${event.economicLoss}B`} size="small" sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 600 }} />
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={7}>
              {selectedEvent ? (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {getEventTypeIcon(selectedEvent.type)} {selectedEvent.name}
                      </Typography>
                      <IconButton onClick={() => setSelectedEvent(null)} size="small"><ExpandLessIcon /></IconButton>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>{selectedEvent.description}</Typography>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={4}>
                        <Paper sx={{ p: 1.5, bgcolor: '#FEE2E2', textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#991B1B' }}>${selectedEvent.economicLoss}B</Typography>
                          <Typography variant="caption">Economic Loss</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper sx={{ p: 1.5, bgcolor: '#FEF3C7', textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#92400E' }}>${selectedEvent.insuredLoss}B</Typography>
                          <Typography variant="caption">Insured Loss</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper sx={{ p: 1.5, bgcolor: '#E0E7FF', textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#3730A3' }}>{selectedEvent.fatalities.toLocaleString()}</Typography>
                          <Typography variant="caption">Fatalities</Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Market Indicator</InputLabel>
                      <Select value={selectedIndicator} label="Market Indicator" onChange={(e) => setSelectedIndicator(e.target.value)}>
                        {selectedEvent.marketImpacts.map((impact) => (
                          <MenuItem key={impact.indicator} value={impact.indicator}>
                            {impact.name} ({formatPercent(impact.changePercent)})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {chartData.length > 0 && (
                      <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="dateLabel" tick={{ fontSize: 10 }} interval={Math.floor(chartData.length / 8)} />
                          <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10 }} />
                          <RechartsTooltip />
                          <ReferenceArea x1={chartData.find(d => d.isEventPeriod)?.dateLabel} x2={chartData.filter(d => d.isEventPeriod).pop()?.dateLabel}
                            fill={getEventTypeColor(selectedEvent.type)} fillOpacity={0.15} />
                          <Area type="monotone" dataKey="value" stroke={getEventTypeColor(selectedEvent.type)}
                            fill={getEventTypeColor(selectedEvent.type)} fillOpacity={0.2} strokeWidth={2} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}

                    <Paper sx={{ p: 2, bgcolor: '#FEF3C7', border: '1px solid #F59E0B', mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#92400E' }}>💡 Key Lesson</Typography>
                      <Typography variant="body2" sx={{ color: '#78350F' }}>{selectedEvent.lessonLearned}</Typography>
                    </Paper>
                  </CardContent>
                </Card>
              ) : (
                <Card sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <TimelineIcon sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: '#6B7280' }}>Select an event to view details</Typography>
                  </Box>
                </Card>
              )}
            </Grid>
          </Grid>
        </>
      )}

      {/* TAB 1: WHAT-IF SIMULATOR */}
      {activeTab === 1 && (
        <>
          <Card sx={{ mb: 3, border: '2px solid #8B5CF6' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                <ScienceIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#8B5CF6' }} />
                What-If Scenario Simulator
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
                Simulate how historical climate events would impact your portfolio today.
              </Typography>

              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: '#F9FAFB' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Portfolio Value</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{formatCurrency(portfolioMetrics.totalValue)}</Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      {portfolioMetrics.items.length} locations in {portfolioMetrics.states.length} states
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Severity Multiplier</Typography>
                  <Slider value={customSeverity} onChange={(e, v) => setCustomSeverity(v as number)} valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v}%`} min={50} max={200}
                    marks={[{ value: 50, label: '50%' }, { value: 100, label: '100%' }, { value: 200, label: '200%' }]} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel control={<Switch checked={showAllEvents} onChange={(e) => setShowAllEvents(e.target.checked)} />}
                    label="Show all events" />
                  <Button variant="contained" startIcon={<PlayIcon />} onClick={runSimulation} fullWidth
                    sx={{ mt: 1, bgcolor: '#8B5CF6', '&:hover': { bgcolor: '#7C3AED' } }}>
                    Run Simulation
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {simulationResults.length > 0 && (
            <>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Card sx={{ bgcolor: '#FEE2E2' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#991B1B' }}>
                        {formatCurrency(Math.max(...simulationResults.map(r => r.estimatedLoss)))}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#991B1B' }}>Worst Case Loss</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ bgcolor: '#FEF3C7' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#92400E' }}>
                        {formatCurrency(simulationResults.reduce((sum, r) => sum + r.estimatedLoss, 0) / simulationResults.length)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#92400E' }}>Average Loss</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ bgcolor: '#D1FAE5' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#065F46' }}>
                        {Math.max(...simulationResults.map(r => r.lossPercent)).toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#065F46' }}>Max Portfolio Impact</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card sx={{ bgcolor: '#E0E7FF' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#3730A3' }}>
                        {Math.max(...simulationResults.map(r => r.recoveryDays))} days
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#3730A3' }}>Longest Recovery</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Scenario Impact Analysis</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Type</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Exposed Value</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Locations</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Est. Loss</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>% Portfolio</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Recovery</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {simulationResults.slice(0, 15).map((result) => (
                          <TableRow key={result.event.id} sx={{ bgcolor: result.lossPercent > 5 ? '#FEF2F2' : 'inherit' }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography>{getEventTypeIcon(result.event.type)}</Typography>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{result.event.name}</Typography>
                                  <Typography variant="caption" sx={{ color: '#6B7280' }}>{new Date(result.event.date).getFullYear()}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={result.event.type} size="small" sx={{ bgcolor: getEventTypeColor(result.event.type), color: 'white', fontSize: '0.65rem', textTransform: 'capitalize' }} />
                            </TableCell>
                            <TableCell align="right">{formatCurrency(result.exposedValue)}</TableCell>
                            <TableCell align="right">{result.exposedLocations}</TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontWeight: 600, color: '#991B1B' }}>{formatCurrency(result.estimatedLoss)}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={`${result.lossPercent.toFixed(1)}%`} size="small"
                                sx={{ bgcolor: result.lossPercent > 10 ? '#FEE2E2' : result.lossPercent > 5 ? '#FEF3C7' : '#D1FAE5',
                                  color: result.lossPercent > 10 ? '#991B1B' : result.lossPercent > 5 ? '#92400E' : '#065F46', fontWeight: 600 }} />
                            </TableCell>
                            <TableCell align="right">{result.recoveryDays} days</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Potential Loss by Scenario</Typography>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={simulationResults.slice(0, 12)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="event.name" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" height={90} />
                      <YAxis tickFormatter={(v) => formatCurrency(v)} />
                      <RechartsTooltip formatter={(value: number) => [formatCurrency(value), 'Est. Loss']} />
                      <Bar dataKey="estimatedLoss" radius={[4, 4, 0, 0]}>
                        {simulationResults.slice(0, 12).map((entry, index) => (
                          <Cell key={index} fill={getEventTypeColor(entry.event.type)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #E5E7EB' }}>
        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
          Data: NOAA NCEI Billion-Dollar Disasters, FRED, insurance reports. Simulations are estimates, not financial advice.
        </Typography>
      </Box>
    </Box>
  );
};

export default MarketBacktesting;
